import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Code, 
  RefreshCw
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import DatePicker from 'react-datepicker';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  MAX_ANALYTICS_RANGE_DAYS,
  applyDateRangeChange,
  getMaxEndDate,
  getQuickDateRange
} from '../utils/dateRange';
import {
  formatDecimalMetric,
  formatIntegerMetric,
  metricNumber
} from '../utils/dailyMetrics';
import "react-datepicker/dist/react-datepicker.css";

const Dashboard = () => {
  const [loading, setLoading] = useState(true); // General loading for initial setup
  const [kpis, setKpis] = useState(null);
  const [dailyTrends, setDailyTrends] = useState([]);
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [teams, setTeams] = useState([]);
  
  // Keep track of loading states for different parts of the dashboard
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [loadingTrends, setLoadingTrends] = useState(true);
  const [loadingTeams, setLoadingTeams] = useState(true);

  // Initialize dates without brittle client-side TZ math; backend enforces ET on queries
  const [dateRange, setDateRange] = useState(() => ({
    startDate: startOfDay(subDays(new Date(), 14)),
    endDate: endOfDay(new Date())
  }));
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [isMobile, setIsMobile] = useState(false);
  const [includeIT, setIncludeIT] = useState(true);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadAnalyticsData = useCallback(async () => {
    try {
      if (!dateRange.startDate || !dateRange.endDate) {
        toast.error('Please select a valid date range');
        return;
      }
      
      const params = {
        startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
        endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
        team: selectedTeam,
        includeIT: includeIT
      };

      // Set all loading states to true before fetching
      setLoadingKpis(true);
      setLoadingTrends(true);
      setLoadingTeams(true);

      const kpisPromise = api.get('/analytics/kpis', { params }).then(res => setKpis(res.data)).finally(() => setLoadingKpis(false));
      const trendsPromise = api.get('/analytics/daily-trends', { params }).then(res => {
        setDailyTrends(Array.isArray(res.data) ? res.data : []);
      }).finally(() => setLoadingTrends(false));
      const teamsPromise = api.get('/analytics/teams', { params }).then(res => {
          setTeamPerformance(Array.isArray(res.data?.teams) ? res.data.teams : []);
          // Also fetch the list of all teams for the dropdown
          api.get(endpoints.analytics.teams).then(res => setTeams(res.data.teams || []));
      }).finally(() => setLoadingTeams(false));

      await Promise.all([kpisPromise, trendsPromise, teamsPromise]);

    } catch (error) {
      console.error('Failed to load analytics data:', error);
      toast.error('Failed to load analytics data');
      // Set all loading states to false on error to stop spinners
      setLoadingKpis(false);
      setLoadingTrends(false);
      setLoadingTeams(false);
    } finally {
      setLoading(false); 
    }
  }, [selectedTeam, dateRange, includeIT]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // Only load data when both dates are selected
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      loadAnalyticsData();
    }
  }, [dateRange, loadAnalyticsData]);

  const handleDateRangeChange = (field, date) => {
    const result = applyDateRangeChange(dateRange, field, date);
    if (!result.ok) {
      if (result.error) toast.error(result.error);
      return;
    }
    setDateRange(result.range);
  };

  const handleQuickDateRange = (days) => {
    setDateRange(getQuickDateRange(days));
    
    // Trigger data reload immediately for quick date selections
    setTimeout(() => loadAnalyticsData(), 0);
  };

  
  // Process daily trends data to scale active developers to total developers
  const getProcessedDailyTrends = () => {
    if (!dailyTrends || !kpis) return [];
    
    // Get total developers count for reference (used in YAxis domain in the chart)
    return dailyTrends.map(day => {
      // Calculate EWPS for each day using the updated formula (kept consistent with Users.js)
      // EWPS = totalRequests + (0.08 × totalTabsAccepted) + [ ln(1 + totalLinesAdded) / ln(1 + 1000) × 0.5 × totalRequests ]
      
      // Check if the additional fields are present in the data
      // If not, we need to simulate them based on the available data
      let totalAccepts = 0;
      let tabsAccepted = 0;
      
      // Check if the fields exist in the data
      const hasAdditionalFields = 'chatAcceptedLinesAdded' in day && 
                                 'chatTotalAccepts' in day && 
                                 'tabsAccepted' in day;
                                 
      if (hasAdditionalFields) {
        // Use the actual values if they exist
        totalAccepts = metricNumber(day.chatTotalAccepts);
        tabsAccepted = metricNumber(day.tabsAccepted);
      } else {
        // Simulate values based on other metrics
        // Estimate total accepts based on requests
        totalAccepts = Math.round(metricNumber(day.composerRequests) * 0.8 + metricNumber(day.chatRequests) * 0.6);
        
        // Estimate tabs accepted based on chat requests
        tabsAccepted = Math.round(metricNumber(day.chatRequests) * 0.4);
      }
      
      // Debug the values used in EWPS calculation
      console.log(`EWPS calculation for ${day.recordDate}:`, {
        totalRequests: day.totalRequests || 0,
        totalLinesAdded: day.linesAdded || 0,
        totalAccepts,
        tabsAccepted,
        bugbotUsages: day.bugbotUsages || 0,
        hasAdditionalFields
      });
      
      // Calculate EWPS using the updated formula
      const totalRequestsDay = metricNumber(day.totalRequests);
      const normLines = Math.log(1 + metricNumber(day.linesAdded)) / Math.log(1 + 1000);
      const ewps = totalRequestsDay
        + 0.08 * tabsAccepted
        + 0.5 * totalRequestsDay * (isFinite(normLines) && normLines > 0 ? normLines : 0);
                   
      // Log the calculated EWPS vs totalRequests
      console.log(`EWPS result for ${day.recordDate}:`, {
        calculatedEwps: ewps,
        totalRequests: day.totalRequests || 0,
        difference: ewps - (day.totalRequests || 0)
      });
      
      // Create a copy of the original data
      return {
        ...day,
        // Keep the original activeUsers value for tooltips
        originalActiveUsers: day.activeUsers,
        // The activeUsers value is kept as is, scaling is handled by YAxis domain
        activeUsers: metricNumber(day.activeUsers),
        linesAdded: metricNumber(day.linesAdded),
        totalRequests: totalRequestsDay,
        // Add EWPS score
        ewps: Math.round(ewps * 10) / 10 // Round to 1 decimal place
      };
    });
  };
  
  // Responsive chart heights
  const getChartHeight = (type = 'default') => {
    if (isMobile) {
      return type === 'main' ? 250 : 200;
    }
    return type === 'main' ? 350 : 250;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">
            Analytics Dashboard
          </h1>
          <p className="text-secondary-600 mt-1">
            Detailed insights into Cursor AI usage and performance
          </p>
        </div>
        
        {/* Include IT Checkbox and Refresh Button */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4 lg:mt-0">
          <label className="flex items-center gap-2 text-sm text-secondary-700">
            <input
              type="checkbox"
              checked={includeIT}
              onChange={(e) => setIncludeIT(e.target.checked)}
              className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
            />
            Include IT
          </label>
          <button
            onClick={loadAnalyticsData}
            disabled={loading}
            className="btn-primary btn-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range Picker */}
            <div>
              <label className="form-label">Date Range</label>
              <div className="flex items-center gap-2">
                <DatePicker
                  selected={dateRange.startDate}
                  onChange={(date) => handleDateRangeChange('startDate', date)}
                  selectsStart
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  maxDate={dateRange.endDate || endOfDay(new Date())}
                  dateFormat="MMM dd, yyyy"
                  className="input flex-1"
                  placeholderText="From date"
                />
                <span className="text-gray-500">to</span>
                <DatePicker
                  selected={dateRange.endDate}
                  onChange={(date) => handleDateRangeChange('endDate', date)}
                  selectsEnd
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  minDate={dateRange.startDate}
                  maxDate={getMaxEndDate(dateRange.startDate)}
                  dateFormat="MMM dd, yyyy"
                  className="input flex-1"
                  placeholderText="To date"
                />
              </div>
              <p className="text-xs text-secondary-500 mt-1">
                Max {MAX_ANALYTICS_RANGE_DAYS} days per range. Earlier start dates auto-adjust the end date; you can shorten it.
              </p>
            </div>

            {/* Quick Date Filters */}
            <div>
              <label className="form-label">Quick Select</label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleQuickDateRange(7)}
                  className="btn-outline btn-sm"
                >
                  7d
                </button>
                <button
                  onClick={() => handleQuickDateRange(14)}
                  className="btn-outline btn-sm"
                >
                  14d
                </button>
                <button
                  onClick={() => handleQuickDateRange(30)}
                  className="btn-outline btn-sm"
                >
                  30d
                </button>
              </div>
            </div>

            {/* Metric Filter */}
            <div>
              <label className="form-label">Metric</label>
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className="input w-full"
              >
                <option value="all">All Metrics</option>
                <option value="lines">Lines Added</option>
                <option value="actions">Actions</option>
                <option value="users">Active Developers</option>
              </select>
            </div>

            {/* Team Filter */}
            <div>
              <label className="form-label">Team</label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="input w-full"
              >
                <option value="all">All Teams</option>
                {teams.map((team) => (
                  <option key={team.teamId} value={team.teamId}>
                    {team.teamName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {loadingKpis ? (
          Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="stat-card animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))
        ) : (
          <>
            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">{kpis?.totalUsers || 0}</div>
                  <div className="stat-label">Total Developers</div>
                </div>
                <Users className="w-8 h-8 text-primary-600" />
              </div>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">{kpis?.activeUsers || 0}</div>
                  <div className="stat-label">Active Developers</div>
                </div>
                <TrendingUp className="w-8 h-8 text-success-600" />
              </div>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">
                    {formatIntegerMetric(kpis?.totalLinesAdded)}
                  </div>
                  <div className="stat-label">Lines Added</div>
                </div>
                <Code className="w-8 h-8 text-warning-600" />
              </div>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">
                    {formatIntegerMetric(kpis?.totalActions)}
                  </div>
                  <div className="stat-label">Total Requests</div>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-600" />
              </div>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="stat-value">
                    {formatDecimalMetric(kpis?.avgActionsPerUser, 1)}
                  </div>
                  <div className="stat-label">Avg Requests/User</div>
                </div>
                <TrendingUp className="w-8 h-8 text-indigo-600" />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Time Series Chart */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-secondary-900">
            Usage Trends Over Time
          </h3>
          <p className="text-sm text-secondary-600">
            Daily active developers, lines added, and total requests
          </p>
        </div>
        <div className="card-body">
          {loadingTrends ? (
            <div className="flex items-center justify-center" style={{ height: getChartHeight('main') }}>
              <LoadingSpinner />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={getChartHeight('main')}>
              <LineChart data={getProcessedDailyTrends()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="recordDate" 
                  tickFormatter={(date) => {
                    // Format date properly without timezone compensation hacks
                    if (!date) return '';
                    try {
                      // Backend now handles timezone correctly, so no need for +1 day hack
                      let dateObj;
                      if (typeof date === 'string') {
                        // Remove any time portion if present
                        const dateOnly = date.split('T')[0];
                        dateObj = new Date(dateOnly + 'T12:00:00'); // Use noon to avoid timezone edge cases
                      } else {
                        dateObj = date;
                      }
                      // Only show month and day, no time
                      return format(dateObj, 'MMM dd');
                    } catch (e) {
                      console.error('Error formatting date:', date, e);
                      return date; // Return original value if parsing fails
                    }
                  }}
                />
                <YAxis 
                  yAxisId="left" 
                  domain={[0, metricNumber(kpis?.totalUsers) || 135]}
                  ticks={[0, Math.round((metricNumber(kpis?.totalUsers) || 135) / 4), 
                         Math.round((metricNumber(kpis?.totalUsers) || 135) / 2), 
                         Math.round(3 * (metricNumber(kpis?.totalUsers) || 135) / 4), 
                         metricNumber(kpis?.totalUsers) || 135]}
                  label={{ value: 'Developers', angle: -90, position: 'insideLeft', dy: 50 }}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right"
                  domain={[0, 'auto']}
                  label={{ value: 'Lines Added', angle: 90, position: 'insideRight', dy: -50 }}
                />
                <YAxis 
                  yAxisId="requests"
                  orientation="right"
                  hide={true}
                  domain={[0, 'auto']}
                />
                <Tooltip 
                  formatter={(value, name, props) => {
                    // Show original activeUsers value in tooltip
                    if (name === "Active Developers" && props.payload.originalActiveUsers !== undefined) {
                      return [props.payload.originalActiveUsers, name];
                    }
                    
                    // For Total Requests, show the breakdown by request type
                    if (name === "Total Requests") {
                      const day = props.payload;
                      const composerReqs = day.composerRequests || 0;
                      const chatReqs = day.chatRequests || 0;
                      const agentReqs = day.agentRequests || 0;
                      const cmdKReqs = day.cmdKUsages || 0;
                      const bugbotReqs = day.bugbotUsages || 0;
                      
                      return [
                        `${value} (Composer: ${composerReqs}, Chat: ${chatReqs}, Agent: ${agentReqs}, Cmd+K: ${cmdKReqs}, Bugbot: ${bugbotReqs})`,
                        name
                      ];
                    }
                    
                    return [value, name];
                  }}
                  labelFormatter={(date) => {
                    // Format tooltip date properly without timezone hacks
                    if (!date) return '';
                    try {
                      // Backend now handles timezone correctly, so no need for +1 day hack
                      let dateObj;
                      if (typeof date === 'string') {
                        // Remove any time portion if present
                        const dateOnly = date.split('T')[0];
                        dateObj = new Date(dateOnly + 'T12:00:00'); // Use noon to avoid timezone edge cases
                      } else {
                        dateObj = date;
                      }
                      // Only show month and day, no time
                      return format(dateObj, 'MMM dd');
                    } catch (e) {
                      console.error('Error formatting tooltip date:', date, e);
                      return date; // Return original value if parsing fails
                    }
                  }}
                />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="activeUsers" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  name="Active Developers"
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="linesAdded" 
                  stroke="#22c55e" 
                  strokeWidth={2}
                  name="Lines Added"
                />
                <Line 
                  yAxisId="requests"
                  type="monotone" 
                  dataKey="totalRequests" 
                  stroke="#9333ea" 
                  strokeWidth={2}
                  name="Total Requests"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Team Performance Table */}
      <div className="card">
        <div className="card-header">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900">
              Team Productivity Leaderboard
            </h3>
            <p className="text-sm text-secondary-600">
              Top 10 teams ranked by lines added per user (productivity metric)
            </p>
          </div>
        </div>
        <div className="card-body">
          {loadingTeams ? (
            <div className="text-center py-8">
              <LoadingSpinner />
              <p className="text-secondary-600 mt-2">Loading team performance...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table min-w-full">
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>Users</th>
                    <th>Lines Added</th>
                    <th>Lines Added/User</th>
                    <th>Total Requests</th>
                    <th>Avg Requests/User</th>
                    <th>Engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {teamPerformance
                    .filter(team => includeIT || (team.teamName !== 'Maverick IT'))
                    .sort((a, b) => {
                      const linesPerUserA = metricNumber(a.totalUsers) > 0 ? metricNumber(a.totalLinesAdded) / metricNumber(a.totalUsers) : 0;
                      const linesPerUserB = metricNumber(b.totalUsers) > 0 ? metricNumber(b.totalLinesAdded) / metricNumber(b.totalUsers) : 0;
                      return linesPerUserB - linesPerUserA; // Sort descending (highest first)
                    })
                    .slice(0, 10)
                    .map((team, index) => {
                    const totalRequests = metricNumber(team.totalRequests);
                    const totalUsers = metricNumber(team.totalUsers);
                    const avgRequestsPerUser = team.avgRequestsPerUser != null
                      ? metricNumber(team.avgRequestsPerUser)
                      : (totalUsers > 0 ? Math.round(totalRequests / totalUsers) : 0);
                    const linesAddedPerUser = totalUsers > 0 ? metricNumber(team.totalLinesAdded) / totalUsers : 0;
                    
                    return (
                      <tr key={index}>
                        <td className="font-medium">{team.teamName || 'Unassigned'}</td>
                        <td>{totalUsers}</td>
                        <td>{formatIntegerMetric(team.totalLinesAdded)}</td>
                        <td className="font-semibold text-primary-600">{formatDecimalMetric(linesAddedPerUser, 1)}</td>
                        <td>{formatIntegerMetric(totalRequests)}</td>
                        <td>{formatDecimalMetric(avgRequestsPerUser, 1)}</td>
                        <td>
                          <div className="flex items-center">
                            <div className="w-20 bg-secondary-200 rounded-full h-2 mr-2">
                              <div 
                                className="bg-primary-600 h-2 rounded-full" 
                                style={{ 
                                  width: `${Math.min(team.engagementRate || 0, 100)}%` 
                                }}
                              />
                            </div>
                            <span className="text-sm text-secondary-600">
                              {Math.round(team.engagementRate || 0)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;